import { useEffect, useState } from "react";
import { Mail, Clock } from "lucide-react";
import SEO from "@/components/SEO";
import LeadForm from "@/components/LeadForm";
import { Reveal, Eyebrow } from "@/components/Reveal";

export default function Contact() {
  const [prefill, setPrefill] = useState(null);

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem("vector_prefill");
      if (raw) {
        setPrefill(JSON.parse(raw));
        sessionStorage.removeItem("vector_prefill");
      }
    } catch (e) {
      /* ignore malformed prefill */
    }
  }, []);

  return (
    <>
      <SEO
        title="Contact — Vector Growth and Advisory"
        description="Get a growth audit from Vector Growth and Advisory — digital acquisition funnels, AI-CRM automation and market intelligence for European enterprises."
      />
      <section className="relative overflow-hidden pt-40 pb-24 md:pt-48" data-testid="contact-page">
        <div className="spot pointer-events-none absolute inset-0" />
        <div className="relative mx-auto grid max-w-[1440px] gap-16 px-6 md:px-10 lg:grid-cols-2">
          <Reveal>
            <Eyebrow>Contact</Eyebrow>
            <h1 className="mt-6 font-display text-4xl font-extrabold tracking-tight sm:text-6xl" data-testid="contact-headline">
              Point your pipeline <span className="text-gradient">somewhere better.</span>
            </h1>
            <p className="mt-6 max-w-md text-base leading-relaxed text-slate-500">
              Tell us where your growth stands and where it should be. We reply within one
              business day with a first read on the opportunity.
            </p>

            <div className="mt-12 space-y-5" data-testid="contact-details">
              <a
                href="mailto:aadibanerjee@gmail.com"
                data-testid="contact-email-link"
                className="group flex items-center gap-4 rounded-xl border hairline bg-surface/60 p-5 transition-all duration-300 hover:border-neon/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-neon/30 bg-neon/5">
                  <Mail className="h-4 w-4 text-neon" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Email</span>
                  <span className="mt-1 block text-sm text-slate-700 group-hover:text-neon">aadibanerjee@gmail.com</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-xl border hairline bg-surface/60 p-5" data-testid="contact-response-time">
                <span className="flex h-11 w-11 items-center justify-center rounded-full border border-neon/30 bg-neon/5">
                  <Clock className="h-4 w-4 text-neon" />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">Response time</span>
                  <span className="mt-1 block text-sm text-slate-700">Within one business day</span>
                </span>
              </div>
            </div>

            <p className="mt-10 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600" data-testid="contact-bot-hint">
              Prefer instant answers? Ask VECTOR, our AI advisor — bottom-right corner.
            </p>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-2xl border hairline bg-surface/70 p-8 md:p-10" data-testid="contact-form-card">
              <p className="font-mono text-[11px] uppercase tracking-[0.24em] text-neon/90">Growth audit request</p>
              <h2 className="mt-3 font-display text-2xl font-extrabold tracking-tight">Start the conversation</h2>
              <div className="mt-8">
                {prefill && (
                  <div className="mb-6 rounded-lg border border-neon/30 bg-neon/5 px-4 py-3 text-xs leading-relaxed text-neon" data-testid="contact-prefill-banner">
                    Drafted from your VECTOR chat — check and edit anything before sending.
                  </div>
                )}
                <LeadForm prefill={prefill} key={prefill ? "prefilled" : "blank"} />
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
